import type { CaptureResult } from "posthog-js";

export const EVIDENCE_RANK_PRESENTED_EVENT =
  "evidence_rank_presented" as const;

/**
 * Exact custom-property contract for the Evidence Rank presentation event.
 * PostHog's final before_send hook applies this list after SDK super-properties
 * have been merged.
 */
export const EVIDENCE_RANK_PRESENTED_CUSTOM_PROPERTY_KEYS = [
  "evidence_rank_contract_version",
  "evidence_rank_code",
  "evidence_rank_label",
  "leading_evidence_lane",
  "assessment_baseline_status",
  "assessment_baseline_resolved",
  "opinion_available",
  "opinion_eligible_comparable_count_bucket",
  "supplemental_comparable_count_bucket",
  "ranked_lane_count_bucket",
  "process_readiness_status",
  "filing_guide_verified",
  "submission_rule_verified",
  "wizard_variant",
  "customer_identity_mode",
  "county_depth_event_contract_version",
  "county_resolution_mode",
  "guide_depth",
  "supported_county_config",
  "cie_capability_depth",
  "county_operating_availability",
  "packet_depth",
  "county_quality_status",
  "county_knowledge_band",
  "source_automation_depth",
  "source_batch_readiness",
] as const;

const ANALYTICS_ENVIRONMENT_PROPERTY_KEYS = [
  "analytics_environment",
  "is_test_account",
  "analytics_traffic_class",
] as const;

export const EVIDENCE_RANK_PRESENTED_PROPERTY_KEYS = [
  ...EVIDENCE_RANK_PRESENTED_CUSTOM_PROPERTY_KEYS,
  ...ANALYTICS_ENVIRONMENT_PROPERTY_KEYS,
] as const;

/**
 * Minimal PostHog transport fields retained so the event can be ingested and
 * deduplicated. These are SDK mechanics, not FairPath evidence properties.
 */
const REQUIRED_POSTHOG_PROPERTY_KEYS = [
  "token",
  "distinct_id",
  "$insert_id",
  "$lib",
  "$lib_version",
  "$process_person_profile",
] as const;

const ALLOWED_EVIDENCE_RANK_POSTHOG_PROPERTIES = new Set<string>([
  ...EVIDENCE_RANK_PRESENTED_PROPERTY_KEYS,
  ...REQUIRED_POSTHOG_PROPERTY_KEYS,
]);

/**
 * Enforce the Evidence Rank contract on the final SDK payload. This runs after
 * PostHog has merged registered attribution and automatic URL properties, so
 * mail codes, county ids, QR ids, property routes, and unrelated super-
 * properties cannot leave the browser on this event.
 */
export function enforceEvidenceRankPostHogPrivacy(
  capture: CaptureResult | null,
): CaptureResult | null {
  if (!capture || capture.event !== EVIDENCE_RANK_PRESENTED_EVENT) {
    return capture;
  }

  return {
    uuid: capture.uuid,
    event: capture.event,
    ...(capture.timestamp ? { timestamp: capture.timestamp } : {}),
    properties: Object.fromEntries(
      Object.entries(capture.properties).filter(([key]) =>
        ALLOWED_EVIDENCE_RANK_POSTHOG_PROPERTIES.has(key)
      ),
    ),
  };
}
